// Twilio Voice SDK provider — outbound PSTN calling for CRM agents.
//
// Owns the Voice SDK Device lifecycle:
//   - On mount (once an agent is authenticated) fetches a token from
//     POST /twilio/voice/token, instantiates a Twilio Device, registers
//     it with identity `agent_{agentId}`.
//   - Auto-refreshes the token on the SDK's `tokenWillExpire` event.
//   - Exposes makeCall, hangup, toggleMute, sendDigit + state to the rest
//     of the app via useTwilioVoice().
//   - Cleans up (unregister + destroy) on unmount.
//
// Intentionally outbound-only for Phase 4A. Phase 4B will flip
// incoming-call handling on (the SDK is already initialised with
// incomingAllow: true on the backend Grant, so it's just a matter of
// wiring the "incoming" event to UI here).
//
// Backend URL is read from src/config/express.json the same way
// VoiceToText.tsx does. Auth uses the existing `accessToken` from
// localStorage — same pattern as audio transcription.

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useSnackbar } from "notistack";
import { Device, Call } from "@twilio/voice-sdk";
import expressConfig from "src/config/express.json";
import useActiveUserDetail from "src/utils/shared/useActiveUserDetail";

type CallState =
  | "uninitialized"
  | "registering"
  | "ready"
  | "calling"
  | "in-call"
  | "incoming"
  | "error";

export interface IncomingCallEntry {
  callSid: string;
  from: string | null;
  customerName: string | null;
}

interface TwilioVoiceContextType {
  // State
  callState: CallState;
  identity: string | null;
  remoteParty: string | null; // E.164 number we're talking to
  remotePartyName: string | null; // Resolved customer name when known
  // Queue of all currently-ringing inbound calls. Each customer that
  // dialled while the agent was free or busy appears here until they
  // either get accepted, rejected, hang up, or time out (Twilio's
  // <Dial timeout> on our TwiML — 30s today).
  incomingQueue: IncomingCallEntry[];
  durationSeconds: number;
  formattedDuration: string;
  isMuted: boolean;
  lastError: string | null;
  isReady: boolean;
  isOnCall: boolean;
  hasIncoming: boolean;
  // Methods. accept/reject now take a CallSid so the agent can choose
  // which of several queued calls to act on.
  makeCall: (toNumber: string) => Promise<void>;
  hangup: () => void;
  toggleMute: () => void;
  sendDigit: (digit: string) => void;
  acceptIncoming: (callSid: string) => void;
  rejectIncoming: (callSid: string) => void;
}

const TwilioVoiceContext = createContext<TwilioVoiceContextType | undefined>(
  undefined,
);

function buildBackendUrl(): string {
  const env =
    (process.env.REACT_APP_ENV as keyof typeof expressConfig) ||
    (process.env.NODE_ENV as keyof typeof expressConfig) ||
    "development";
  const cfg = (expressConfig as any)[env] || (expressConfig as any).development;
  const port = cfg?.port ? `:${cfg.port}` : "";
  const domain = cfg?.graphql_domain || "";
  return `${domain}${port}`;
}

async function fetchVoiceToken(): Promise<{
  token: string;
  identity: string;
  expiresIn: number;
}> {
  const url = `${buildBackendUrl()}/twilio/voice/token`;
  const accessToken = localStorage.getItem("accessToken");
  if (!accessToken) {
    throw new Error("Not authenticated");
  }
  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: "{}",
    credentials: "include",
  });
  if (!res.ok) {
    // Backend returns { error: "Twilio number not connected" } etc.
    // Prefer the specific reason so the UI (tooltip, snackbar) can show
    // exactly why voice isn't available. Fall back to status text only
    // if the body isn't JSON.
    const text = await res.text().catch(() => "");
    let specific = "";
    try {
      const parsed = text ? JSON.parse(text) : null;
      specific = parsed?.error || "";
    } catch {
      /* not JSON — leave specific empty */
    }
    throw new Error(specific || text || res.statusText || "Token fetch failed");
  }
  return res.json();
}

function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

export const TwilioVoiceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { agentId } = useActiveUserDetail();

  const [callState, setCallState] = useState<CallState>("uninitialized");
  const [identity, setIdentity] = useState<string | null>(null);
  const [remoteParty, setRemoteParty] = useState<string | null>(null);
  const [remotePartyName, setRemotePartyName] = useState<string | null>(null);
  const [incomingQueue, setIncomingQueue] = useState<IncomingCallEntry[]>([]);
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  const deviceRef = useRef<Device | null>(null);
  const currentCallRef = useRef<Call | null>(null);
  // Map of CallSid → SDK Call object for every ringing inbound call.
  // Mirrors the `incomingQueue` state but lets us look up the raw Call
  // object inside accept/reject without searching the queue array.
  const incomingCallsRef = useRef<Map<string, Call>>(new Map());
  // Mirror of incomingQueue length so device-side callbacks can decide
  // whether to drop back to "ready" or stay in "incoming" without
  // closing over stale state.
  const incomingQueueCountRef = useRef(0);
  const callStartedAt = useRef<number | null>(null);
  const durationInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  // True while makeCall is mid-flight (destroying old device, registering
  // new one, awaiting device.connect). Device events fired during that
  // window would otherwise flip callState away from "calling" and hide
  // the modal until the customer answers. Guarded event handlers below
  // read this ref and no-op during a dial.
  const dialingRef = useRef(false);

  // Remove a single call from the queue + ref-map. Called from the
  // Call's cancel/disconnect/error listeners AND from accept/reject.
  const removeFromQueue = useCallback((callSid: string) => {
    incomingCallsRef.current.delete(callSid);
    setIncomingQueue((prev) => {
      const next = prev.filter((entry) => entry.callSid !== callSid);
      incomingQueueCountRef.current = next.length;
      return next;
    });
  }, []);

  // ──────────────────────────────────────────────────────────────────
  // Device lifecycle
  // ──────────────────────────────────────────────────────────────────

  const teardownDevice = useCallback(async () => {
    if (durationInterval.current) {
      clearInterval(durationInterval.current);
      durationInterval.current = null;
    }
    if (currentCallRef.current) {
      try {
        currentCallRef.current.disconnect();
      } catch {
        /* noop */
      }
      currentCallRef.current = null;
    }
    // Reject every queued inbound so the customers fall through to the
    // fallback message instead of ringing into the void.
    incomingCallsRef.current.forEach((call) => {
      try {
        call.reject();
      } catch {
        /* noop */
      }
    });
    incomingCallsRef.current.clear();
    incomingQueueCountRef.current = 0;
    if (deviceRef.current) {
      try {
        await deviceRef.current.unregister();
        deviceRef.current.destroy();
      } catch (err) {
        // unregister can reject if device wasn't registered yet — fine.
      }
      deviceRef.current = null;
    }
    setCallState("uninitialized");
    setIdentity(null);
    setRemoteParty(null);
    setRemotePartyName(null);
    setIncomingQueue([]);
    setDurationSeconds(0);
    setIsMuted(false);
  }, []);

  // Attach SDK event listeners that depend on `device` instance.
  const attachDeviceListeners = useCallback(
    (device: Device) => {
      device.on("registered", () => {
        // Ignore during an in-flight dial — makeCall spins up a fresh
        // device, so the newly-registered event would otherwise flip
        // callState from "calling" to "ready" and hide the modal until
        // the customer picks up.
        if (dialingRef.current) return;
        setCallState("ready");
        setLastError(null);
      });
      device.on("unregistered", () => {
        // Same reasoning as "registered" — during a dial we deliberately
        // destroy the old device, and that fires unregistered. Don't
        // let it clobber the "calling" state.
        if (dialingRef.current) return;
        // Don't blow away an active call — just note it.
        if (!currentCallRef.current) setCallState("uninitialized");
      });
      device.on("error", (err: any) => {
        const msg = err?.message || String(err);
        setLastError(msg);
        // Most SDK errors are transient. Only flip to error if we have no call.
        if (!currentCallRef.current) setCallState("error");
        // Don't spam the user with every transient connection error.
        if (err?.code && err.code !== 31005) {
          enqueueSnackbar(`Voice: ${msg}`, { variant: "error" });
        }
      });
      device.on("tokenWillExpire", async () => {
        try {
          const { token } = await fetchVoiceToken();
          device.updateToken(token);
        } catch (err: any) {
          setLastError(err?.message || "Token refresh failed");
        }
      });
      // Inbound call routed to this agent via TwiML <Client>agent_X</Client>.
      // Each invocation = one ringing customer. We queue them all so the
      // agent can pick whichever one they want; auto-rejecting silently
      // (the old behaviour) hid backups from the agent.
      device.on("incoming", (call: Call) => {
        const callSid =
          (call.parameters && (call.parameters as any).CallSid) || null;
        if (!callSid) {
          // No CallSid → can't track this in the queue safely. Reject
          // and bail (should never happen in practice).
          try {
            call.reject();
          } catch {
            /* noop */
          }
          return;
        }

        const from =
          (call.parameters && (call.parameters as any).From) ||
          (call.customParameters &&
            (call.customParameters as any).get?.("From")) ||
          null;
        // <Parameter name="customerName"> attached by inbound TwiML.
        // If the resolver couldn't name the caller, this is null and
        // the modal falls back to the raw number.
        const resolvedName =
          (call.customParameters &&
            (call.customParameters as any).get?.("customerName")) ||
          null;

        incomingCallsRef.current.set(callSid, call);
        setIncomingQueue((prev) => {
          // De-dupe in the rare case Twilio fires `incoming` twice for
          // the same leg (e.g. reconnect mid-ring).
          if (prev.some((e) => e.callSid === callSid)) return prev;
          const next = [...prev, { callSid, from, customerName: resolvedName }];
          incomingQueueCountRef.current = next.length;
          return next;
        });

        // Only flip global state to "incoming" if there's no active
        // call. Otherwise stay in "in-call" / "calling" — the queue
        // still renders in the toast, just alongside the active widget.
        if (!currentCallRef.current) {
          setCallState("incoming");
        }

        // Customer hung up, Twilio timed out, or another agent answered
        // → remove this entry. If the queue is now empty AND no active
        // call, drop back to "ready".
        const cleanupOnEnd = () => {
          removeFromQueue(callSid);
          if (!currentCallRef.current && incomingQueueCountRef.current === 0) {
            setCallState(deviceRef.current ? "ready" : "uninitialized");
          }
        };
        call.on("cancel", cleanupOnEnd);
        call.on("disconnect", cleanupOnEnd);
        call.on("error", (err: any) => {
          const msg = err?.message || String(err);
          setLastError(msg);
          cleanupOnEnd();
        });
      });
    },
    [enqueueSnackbar],
  );

  // Initialise the device once we have an authenticated agent.
  useEffect(() => {
    let cancelled = false;
    // [DIAG] Log every mount / effect-run so we can see if the provider
    // is being re-created mid-call (would masquerade as 31009 spam).
    // eslint-disable-next-line no-console
    console.log(
      `[TwilioVoice DIAG] init effect running — agentId=${agentId} t=${new Date().toISOString()}`,
    );
    if (!agentId) {
      return undefined;
    }

    (async () => {
      try {
        setCallState("registering");
        const { token, identity: id } = await fetchVoiceToken();
        if (cancelled) return;

        const device = new Device(token, {
          logLevel: "warn",
          codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
          closeProtection: true, // warn before tab close during a call
        });

        attachDeviceListeners(device);

        await device.register();
        if (cancelled) {
          device.destroy();
          return;
        }
        deviceRef.current = device;
        setIdentity(id);
        // eslint-disable-next-line no-console
        console.log(
          `[TwilioVoice DIAG] device registered as ${id} t=${new Date().toISOString()}`,
        );
      } catch (err: any) {
        if (cancelled) return;
        const msg = err?.message || "Failed to initialise voice device";
        setLastError(msg);
        setCallState("error");
        // Don't snackbar — first-time setup errors are often config-related
        // and noisy; surface in widget instead. Console.error for debug.
        // eslint-disable-next-line no-console
        console.error("[TwilioVoice] init failed:", err);
      }
    })();

    return () => {
      cancelled = true;
      // [DIAG] If this fires while you're mid-call, the SDK gets torn
      // down and all 31009 / stuck-Calling symptoms follow. Look for the
      // agentId in the message — if it matches the current agent, the
      // effect is re-running for some reason (agentId identity change,
      // hot reload, or a parent re-mount).
      // eslint-disable-next-line no-console
      console.warn(
        `[TwilioVoice DIAG] teardown running — agentId=${agentId} hasActiveCall=${!!currentCallRef.current} t=${new Date().toISOString()}`,
      );
      // Promise return is intentionally ignored — useEffect cleanup
      // cannot await, and teardown failures are non-recoverable here.
      teardownDevice().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  // ──────────────────────────────────────────────────────────────────
  // Call methods
  // ──────────────────────────────────────────────────────────────────

  const attachCallListeners = useCallback(
    (call: Call) => {
      call.on("accept", () => {
        setCallState("in-call");
        callStartedAt.current = Date.now();
        if (durationInterval.current) clearInterval(durationInterval.current);
        durationInterval.current = setInterval(() => {
          if (callStartedAt.current) {
            setDurationSeconds(
              Math.floor((Date.now() - callStartedAt.current) / 1000),
            );
          }
        }, 1000);
      });
      // After an active call ends, if there are still queued inbound
      // calls (rang while we were busy), surface them by flipping back
      // to "incoming" so the toast renders + ringtone resumes.
      const onActiveEnded = () => {
        currentCallRef.current = null;
        setRemoteParty(null);
        setRemotePartyName(null);
        if (incomingQueueCountRef.current > 0) {
          setCallState(deviceRef.current ? "incoming" : "uninitialized");
        } else {
          setCallState(deviceRef.current ? "ready" : "uninitialized");
        }
      };
      call.on("disconnect", () => {
        if (durationInterval.current) {
          clearInterval(durationInterval.current);
          durationInterval.current = null;
        }
        callStartedAt.current = null;
        setDurationSeconds(0);
        setIsMuted(false);
        onActiveEnded();
      });
      call.on("cancel", onActiveEnded);
      call.on("reject", onActiveEnded);
      call.on("error", (err: any) => {
        const msg = err?.message || String(err);
        setLastError(msg);
        enqueueSnackbar(`Call error: ${msg}`, { variant: "error" });
      });
      call.on("mute", (muted: boolean) => {
        setIsMuted(muted);
      });
    },
    [enqueueSnackbar],
  );

  // Build a brand-new Device every time. Destroys any existing device,
  // fetches a fresh JWT, spins up a new Device, attaches listeners,
  // registers it, and returns it. Called at the top of makeCall so
  // every outbound dial gets a guaranteed-fresh WebSocket transport
  // and SDP negotiation — no stale session state is ever reused.
  //
  // Why: after long-lived sessions the SDK's internal transport can go
  // stale (dead WebSocket that never reconnected cleanly). Reusing the
  // stale Device makes `.connect()` fail with 31000 "call is no longer
  // valid" — the same reason incognito works (fresh state every time).
  // Forcing a fresh Device per dial reproduces that clean-slate.
  const createFreshDevice = useCallback(async (): Promise<Device> => {
    // Destroy the existing device first. Await unregister so the
    // teardown completes before we spin up a new registration with
    // the same identity — otherwise Twilio would kick the new one
    // out as a duplicate.
    if (deviceRef.current) {
      try {
        await deviceRef.current.unregister();
      } catch {
        /* noop — device may already be unregistered */
      }
      try {
        deviceRef.current.destroy();
      } catch {
        /* noop */
      }
      deviceRef.current = null;
    }

    const { token, identity: id } = await fetchVoiceToken();

    const device = new Device(token, {
      logLevel: "warn",
      codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      closeProtection: true,
    });
    attachDeviceListeners(device);
    await device.register();

    deviceRef.current = device;
    setIdentity(id);
    // eslint-disable-next-line no-console
    console.log(
      `[TwilioVoice DIAG] fresh device registered as ${id} t=${new Date().toISOString()}`,
    );
    return device;
  }, [attachDeviceListeners]);

  const makeCall = useCallback(
    async (toNumber: string) => {
      if (currentCallRef.current) {
        enqueueSnackbar("Already on a call.", { variant: "warning" });
        return;
      }
      const normalised = (toNumber || "").trim();
      if (!/^\+\d{6,15}$/.test(normalised)) {
        enqueueSnackbar("Number must be in E.164 format (e.g. +15131234567).", {
          variant: "error",
        });
        return;
      }

      // Flag the dial-in-progress window so the guarded device event
      // handlers (registered / unregistered) don't overwrite the
      // "calling" state while createFreshDevice is running.
      dialingRef.current = true;
      try {
        // Show "Calling…" immediately so the UI reflects intent while
        // we (a) spin up a fresh Device and (b) place the actual dial.
        // The fresh-device step adds ~700ms–1.2s of latency but
        // guarantees no stale WebSocket / SDP state is reused, which
        // was the root cause of 31000 "call is no longer valid" on
        // repeat calls in the same browser session.
        setCallState("calling");
        setRemoteParty(normalised);

        const device = await createFreshDevice();

        const call = await device.connect({
          params: { To: normalised },
        });
        currentCallRef.current = call;
        attachCallListeners(call);
      } catch (err: any) {
        currentCallRef.current = null;
        setRemoteParty(null);
        setCallState(deviceRef.current ? "ready" : "uninitialized");
        const msg = err?.message || "Failed to place call";
        setLastError(msg);
        enqueueSnackbar(`Call failed: ${msg}`, { variant: "error" });
      } finally {
        // Clear the flag regardless of success/failure so subsequent
        // device events (unregistered on hangup, error events, etc.)
        // resume their normal state-tracking behaviour.
        dialingRef.current = false;
      }
    },
    [attachCallListeners, createFreshDevice, enqueueSnackbar],
  );

  const hangup = useCallback(() => {
    if (currentCallRef.current) {
      try {
        currentCallRef.current.disconnect();
      } catch {
        /* noop */
      }
    }
  }, []);

  const toggleMute = useCallback(() => {
    if (currentCallRef.current) {
      const next = !currentCallRef.current.isMuted();
      currentCallRef.current.mute(next);
      // SDK's "mute" event also flips isMuted; this keeps UI snappy.
      setIsMuted(next);
    }
  }, []);

  const sendDigit = useCallback((digit: string) => {
    if (currentCallRef.current && /^[0-9*#]$/.test(digit)) {
      currentCallRef.current.sendDigits(digit);
    }
  }, []);

  const acceptIncoming = useCallback(
    (callSid: string) => {
      const call = incomingCallsRef.current.get(callSid);
      if (!call) return;
      // Can't accept a second call while one is active. Reject instead,
      // so the queued caller falls through promptly rather than ringing
      // for the rest of the timeout.
      if (currentCallRef.current) {
        enqueueSnackbar("Hang up the current call first.", {
          variant: "warning",
        });
        return;
      }
      // Look up the entry for the name + number BEFORE we remove it,
      // so we can carry them forward into the active-call widget.
      const entry = incomingQueue.find((e) => e.callSid === callSid) || null;
      try {
        call.accept();
        currentCallRef.current = call;
        removeFromQueue(callSid);
        setRemoteParty(entry?.from || null);
        setRemotePartyName(entry?.customerName || null);
        attachCallListeners(call);
      } catch (err: any) {
        const msg = err?.message || "Failed to accept call";
        setLastError(msg);
        enqueueSnackbar(`Accept failed: ${msg}`, { variant: "error" });
      }
    },
    [incomingQueue, attachCallListeners, enqueueSnackbar, removeFromQueue],
  );

  const rejectIncoming = useCallback(
    (callSid: string) => {
      const call = incomingCallsRef.current.get(callSid);
      if (!call) return;
      try {
        call.reject();
      } catch {
        /* noop */
      }
      removeFromQueue(callSid);
      // If the queue is now empty AND no active call, drop to ready.
      if (!currentCallRef.current && incomingQueueCountRef.current === 0) {
        setCallState(deviceRef.current ? "ready" : "uninitialized");
      }
    },
    [removeFromQueue],
  );

  // ──────────────────────────────────────────────────────────────────
  // Context value
  // ──────────────────────────────────────────────────────────────────

  const value = useMemo<TwilioVoiceContextType>(
    () => ({
      callState,
      identity,
      remoteParty,
      remotePartyName,
      incomingQueue,
      durationSeconds,
      formattedDuration: formatDuration(durationSeconds),
      isMuted,
      lastError,
      isReady: callState === "ready",
      isOnCall: callState === "calling" || callState === "in-call",
      hasIncoming: incomingQueue.length > 0,
      makeCall,
      hangup,
      toggleMute,
      sendDigit,
      acceptIncoming,
      rejectIncoming,
    }),
    [
      callState,
      identity,
      remoteParty,
      remotePartyName,
      incomingQueue,
      durationSeconds,
      isMuted,
      lastError,
      makeCall,
      hangup,
      toggleMute,
      sendDigit,
      acceptIncoming,
      rejectIncoming,
    ],
  );

  return (
    <TwilioVoiceContext.Provider value={value}>
      {children}
    </TwilioVoiceContext.Provider>
  );
};

export function useTwilioVoice(): TwilioVoiceContextType {
  const ctx = useContext(TwilioVoiceContext);
  if (!ctx) {
    throw new Error("useTwilioVoice must be used within TwilioVoiceProvider");
  }
  return ctx;
}

export default TwilioVoiceContext;
